import { now } from "@@/shared/date-time/date-time";
import { isoAndUnixZodSchema } from "@@/shared/date-time/iso-and-unix";
import { entityStateZodSchema } from "@@/shared/entity-status";
import { metaContentZodSchema } from "@@/shared/meta-content/meta-content";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import { hexColorZodSchema } from "@@/shared/stylable/hex-color/hex-color";
import { userIdZodSchema } from "@@/shared/users/user-id";
import type { Except } from "type-fest";
import { z } from "zod";
import { pageIdZodSchema } from "../../page-id";
import {
    customContentIdFactory,
    customContentIdZodSchema,
} from "./custom-content-id";

const customContentZodObject = z.object({
    _id: customContentIdZodSchema,
    _type: z.literal("CUSTOM_CONTENT"),
    _createdAt: isoAndUnixZodSchema,
    _editedAt: isoAndUnixZodSchema,
    _editedBy: userIdZodSchema,
    _createdBy: userIdZodSchema,
    _status: entityStateZodSchema,
    pageId: pageIdZodSchema,
    providerId: providerIdZodSchema.optional(),
    meta: metaContentZodSchema,
    backgroundColor: hexColorZodSchema.optional(),
    textColor: hexColorZodSchema.optional(),
    accentColor: hexColorZodSchema.optional(),
    url: z.string().url().optional(),
});
type CustomContent = z.infer<typeof customContentZodObject>;
const customContentZodSchema: z.ZodSchema<CustomContent> =
    customContentZodObject;

type CustomContentFactoryParams = Except<
    CustomContent,
    "_id" | "_type" | "_status" | "_createdAt" | "_editedAt"
> & {
    _createdAt?: CustomContent["_createdAt"];
    _editedAt?: CustomContent["_editedAt"];
};
const customContentFactory = (
    params: CustomContentFactoryParams,
): CustomContent => {
    return {
        _id: customContentIdFactory(),
        _type: "CUSTOM_CONTENT",
        _status: "ACTIVE",
        ...params,
        _createdAt: params._createdAt ?? now(),
        _editedAt: params._editedAt ?? now(),
    };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isCustomContent = (content: any): content is CustomContent => {
    if (!content) return false;
    return "_type" in content && content._type === "CUSTOM_CONTENT";
};

export {
    customContentFactory,
    customContentZodObject,
    customContentZodSchema,
    isCustomContent,
};
export type { CustomContent };
